import { Box, Divider, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { useMemo } from "react"
import { type UseFormProps, useForm } from "react-hook-form"

import { StatusFilter } from "@/components/common/Filters/StatusFilter"
import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import type { InstructionQueryableFields } from "@/graphql/codegen/graphql"
import { InstructionStatus } from "@/screens/ManageProducts/components/Instructions/types"

interface FilterDrawerProps {
  showFilterDrawer: boolean
  toggleDrawer: (open: boolean) => void
}

export type FormValues = {
  isActive: InstructionStatus | InstructionStatus[]
} & InstructionQueryableFields

export const FilterDrawer: FC<FilterDrawerProps> = ({ toggleDrawer, showFilterDrawer }) => {
  const navigate = useNavigate({ from: "/app/admin/manage-products/instructions" })
  const { isActive } = useSearch({
    from: "/app/admin/manage-products/_layout/instructions",
    strict: true,
  })

  const defaultValues = useMemo(
    () => ({
      isActive: isActive !== undefined ? (isActive ? InstructionStatus.Active : InstructionStatus.Inactive) : [],
    }),
    [isActive]
  )

  const methods = useForm<FormValues>({
    values: defaultValues,
    mode: "onChange",
  } as UseFormProps<FormValues>)

  const handleClearFilters = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        // temporary solution with setting a default value until we have instruction statuses,
        // and will have the possibility to set the default status in URL parameter
        isActive: true,
      }),
      replace: true,
    })
  }

  const handleFilterChange = (name: keyof FormValues, value: unknown) => {
    if (name === "isActive") {
      const isActiveValue =
        (value as InstructionStatus[]).length === 1
          ? (value as InstructionStatus[])[0] === InstructionStatus.Active
          : undefined

      navigate({
        search: (prev) => ({
          ...prev,
          isActive: isActiveValue,
        }),
        replace: true,
      })
    }
  }

  return (
    <SearchFilterDrawer
      toggleDrawer={() => toggleDrawer(!showFilterDrawer)}
      open={showFilterDrawer}
      clear={handleClearFilters}
    >
      <Box className='min-w-80 flex-none'>
        <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
          Instruction Status
        </Typography>
        <Divider />
        <StatusFilter
          name='isActive'
          data={Object.values(InstructionStatus)}
          control={methods.control}
          onFilterChange={handleFilterChange}
        />
      </Box>
    </SearchFilterDrawer>
  )
}
